import { dismissAlert } from '@calendly/alert'
import { Form } from '@calendly/form'
import { object, string } from '@calendly/form/validation'
import { I18n } from '@calendly/i18n'
import { Body, Header, Popup, Subtitle, Title } from '@calendly/ui/components/popup'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants'
import { useTrack } from 'hooks/analytics_events/useTrack'

import { countriesWithNoPostalCode } from './countries_with_no_postal_code'
import { FormFooter } from './form_footer'
import { initialValuesSelector } from './selectors'
import {
  clearInvalidFormFields,
  defaultInitialValues, resetPopup,
  setConfirmationVisibility,
  setInitialValues,
  setPopupLocation,
  setShouldUpdateExistingSubscription, setShowThankYouPopup,
  setValidatedFormData,
  setVisibility
} from './slice'
import { TaxForm } from './tax_form'
import { TaxInformationCannotValidateModal } from './tax_information_cannot_validate_modal'
import { TaxInformationConfirmationModal } from './tax_information_confirmation_modal'
import { TaxInformationDeleteModal } from './tax_information_delete_modal'
import { TaxInformationSuccessModal } from './tax_information_success_modal'
import {
  dealsPageStatesByCountrySelector,
  isTaxInformationCannotValidateModalVisibleSelector,
  isTaxInformationConfirmationModalVisibleSelector,
  isTaxInformationDeleteModalVisibleSelector,
  isTaxInformationModalVisibleSelector,
  isTaxInformationSuccessModalVisibleSelector,
  policyStatesByCountrySelector,
  taxInformationModalConfirmedTaxInformationSelector,
  taxInformationModalPopupLocation,
  taxInformationModalShouldUpdateExistingSubscriptionSelector,
  taxInformationModelShowThankYouPopup
} from '../../selectors'
import {
  billingAddressSelector,
  secondaryTaxIdSelector,
  shippingAddressSelector,
  taxIdSelector
} from '../../stages/paid_stage/billing_information_card/selectors'


export const TaxInformationModal = () => {
  const dispatch = useDispatch()
  const track = useTrack()
  const isVisible = useSelector(isTaxInformationModalVisibleSelector)
  const isConfirmationVisible = useSelector(isTaxInformationConfirmationModalVisibleSelector)
  const isSuccessVisible = useSelector(isTaxInformationSuccessModalVisibleSelector)
  const isDeleteVisible = useSelector(isTaxInformationDeleteModalVisibleSelector)
  const isCannotValidateModalVisible = useSelector(isTaxInformationCannotValidateModalVisibleSelector)
  const initialValues = useSelector(initialValuesSelector)
  const billingAddress = useSelector(billingAddressSelector)
  const shippingAddress = useSelector(shippingAddressSelector)
  const taxId = useSelector(taxIdSelector)
  const secondaryTaxId = useSelector(secondaryTaxIdSelector)
  const policyStatesByCountry = useSelector(policyStatesByCountrySelector)
  const dealsStatesByCountry = useSelector(dealsPageStatesByCountrySelector)
  const taxIdPresent = taxId || secondaryTaxId
  const shouldUpdateExistingSubscription = useSelector(taxInformationModalShouldUpdateExistingSubscriptionSelector)
  const confirmedTaxInfo = useSelector(taxInformationModalConfirmedTaxInformationSelector)
  const popupLocation = useSelector(taxInformationModalPopupLocation)
  const showThankYouPopup = useSelector(taxInformationModelShowThankYouPopup)

  useEffect(() => {
    if (isSuccessVisible) {
      track({
        name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
        properties: {
          action: 'confirming_info_success',
          location: 'billing_page',
          modal_name: 'confirm_tax_info'
        }
      })
    }
  }, [isSuccessVisible, track])

  useEffect(() => {
    if (isDeleteVisible) {
      track({
        name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
        properties: {
          action: 'deleting_info',
          location: 'billing_page',
          modal_name: 'edit_tax_info'
        }
      })
    }
  }, [isDeleteVisible, track])

  useEffect(() => {
    if (isConfirmationVisible) {
      const action = () => {
        if (shouldUpdateExistingSubscription) {
          return taxIdPresent ? 'saving_edited_info_success' : 'submitting_info_success'
        }
        return confirmedTaxInfo ? 'saving_edited_info_success' : 'submitting_info_success'
      }

      const modalName = () => {
        if (shouldUpdateExistingSubscription) {
          return taxIdPresent ? 'edit_tax_info' : 'add_tax_info'
        }

        return confirmedTaxInfo ? 'edit_tax_info' : 'add_tax_info'
      }
      track({
        name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
        properties: {
          action: action(),
          location: popupLocation,
          modal_name: modalName()
        }
      })
    }
  }, [confirmedTaxInfo, isConfirmationVisible, popupLocation, shouldUpdateExistingSubscription, taxIdPresent, track])

  if (!isVisible) return false
  if (isCannotValidateModalVisible) return <TaxInformationCannotValidateModal />
  if (isSuccessVisible) {
    if (!showThankYouPopup) return dispatch(resetPopup())

    return <TaxInformationSuccessModal />
  }
  if (isDeleteVisible) return <TaxInformationDeleteModal />
  if (isConfirmationVisible) return <TaxInformationConfirmationModal />

  const statesByCountry = () => policyStatesByCountry || dealsStatesByCountry

  const alphabeticRegex = /^[a-zA-Z\u00C0-\u017F\s]*$/
  const alphanumericRegex = /^[\p{L}\p{N}\p{P}\p{Zs}]*$/u
  const alphanumbericSpecialRegex = /^(?![-' \u00B0])(?!.*[-' \u00B0]$)[\p{L}\p{N}\p{P}\p{Zs}\u00B0]*$/u

  const validationSchema = object({
    countryCode: string()
      .required(I18n.t('publisher.billing.vat_id_popup.form_errors.country_code'))
      .matches(alphabeticRegex, I18n.t('publisher.billing.vat_id_popup.form_errors.country_code')),
    postalCode: string()
      .max(20, I18n.t('publisher.billing.vat_id_popup.form_errors.postal_code'))
      .matches(alphanumericRegex, I18n.t('publisher.billing.vat_id_popup.form_errors.postal_code'))
      .when('countryCode', {
        is: (countryCode) => !countriesWithNoPostalCode.includes(countryCode),
        then: (schema) => schema.required(I18n.t('publisher.billing.vat_id_popup.form_errors.postal_code')),
      }),
    streetAddress: string()
      .required(I18n.t('publisher.billing.vat_id_popup.form_errors.street_address'))
      .max(150, I18n.t('publisher.billing.vat_id_popup.form_errors.street_address')),
    streetAddressTwo: string()
      .max(150, I18n.t('publisher.billing.vat_id_popup.form_errors.street_address_two')),
    city: string()
      .trim()
      .required(I18n.t('publisher.billing.vat_id_popup.form_errors.city'))
      .max(50, I18n.t('publisher.billing.vat_id_popup.form_errors.city'))
      .matches(alphanumbericSpecialRegex, I18n.t('publisher.billing.vat_id_popup.form_errors.city')),
    stateProvince: string()
      .max(50, I18n.t('publisher.billing.vat_id_popup.form_errors.state_province', { count: 50 }))
      .matches(alphabeticRegex, I18n.t('publisher.billing.vat_id_popup.form_errors.state_province'))
      .when('countryCode', {
        is: (value) => ['CA', 'IN'].includes(value),
        then: (schema) => schema.required(I18n.t('publisher.billing.vat_id_popup.form_errors.state_province')),
        otherwise: (schema) => schema.optional(),
      }),
    companyName: string()
      .trim()
      .required(I18n.t('publisher.billing.vat_id_popup.form_errors.company_name'))
      .max(100, I18n.t('publisher.billing.vat_id_popup.form_errors.company_name'))
      .matches(alphanumbericSpecialRegex, I18n.t('publisher.billing.vat_id_popup.form_errors.company_name')),
    taxId: string()
      .when('countryCode', {
        is: (value) => !['CA', 'AU'].includes(value),
        then: (schema) => schema.required(I18n.t('publisher.billing.vat_id_popup.form_errors.tax_id')),
      }),
    canadaFederalTaxId: string()
      .when('countryCode', {
        is: 'CA',
        then: (schema) => schema.test(
          'canadaTaxId',
          I18n.t('publisher.billing.vat_id_popup.form_errors.canada_federal_tax_id'),
          (value, context) => {
            const { canadaProvincialTaxId } = context.parent
            return value || canadaProvincialTaxId // Passes validation if either field is filled
          }
        ),
      }),
    canadaProvincialTaxId: string()
      .when(['countryCode', 'stateProvince'], {
        is: (countryCode, stateProvince) => countryCode === 'CA' && stateProvince !== 'QC',
        then: (schema) => schema.test(
          'canadaTaxId',
          I18n.t('publisher.billing.vat_id_popup.form_errors.canada_provincial_tax_id'),
          (value, context) => {
            const { canadaFederalTaxId } = context.parent
            return value || canadaFederalTaxId // Passes validation if either field is filled
          }
        ),
      })
      .when(['countryCode', 'stateProvince'], {
        is: (countryCode, stateProvince) => countryCode === 'CA' && stateProvince === 'QC',
        then: (schema) => schema.test(
          'canadaTaxId',
          I18n.t('publisher.billing.vat_id_popup.form_errors.canada_qc_tax_id'),
          (value, context) => {
            const { canadaFederalTaxId } = context.parent
            return value || canadaFederalTaxId // Passes validation if either field is filled
          }
        )
      }),
    australiaTaxId: string()
      .when('countryCode', {
        is: 'AU',
        then: (schema) => schema.required(I18n.t('publisher.billing.vat_id_popup.form_errors.australia_tax_id')),
      })
  })

  const openConfirmationPopup = () => {
    dispatch(setConfirmationVisibility(true))
  }

  const handleValidation = (formValues) => {
    // Do validation, and then if successful...

    dispatch(clearInvalidFormFields())
    dispatch(setValidatedFormData(formValues))
    openConfirmationPopup()
  }

  const handleClose = () => {
    const modalName = () => {
      if (shouldUpdateExistingSubscription) {
        return taxIdPresent ? 'edit_tax_info' : 'add_tax_info'
      }

      return confirmedTaxInfo ? 'edit_tax_info' : 'add_tax_info'
    }

    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: 'closing',
        location: popupLocation,
        modal_name: modalName()
      }
    })

    dispatch(dismissAlert()) // Dismiss alert potentially caused by Anrok issue on popup close
    dispatch(setShouldUpdateExistingSubscription(true)) // Reset back to initial state
    dispatch(setInitialValues(defaultInitialValues))
    dispatch(setVisibility(false))
    dispatch(setPopupLocation(null))
    dispatch(setShowThankYouPopup(true))
  }

  const initialValuesOrBillingCountry = () => {
    if (initialValues === defaultInitialValues && taxIdPresent && shippingAddress) {
      const {
        country,
        postal_code: postalCode,
        addr,
        extended_addr: extendedAddr,
        city,
        state,
        company
      } = shippingAddress || {}

      let stateCode = ''

      if (country === 'US' || country === 'CA' || country === 'IN') {
        const availableStates = statesByCountry()[country]

        stateCode = availableStates?.find((s) => s.id === state)?.id
          || availableStates?.find((s) => s.name === state)?.id
          || ''
        // Chargebee saves states as name, not state code... Which is confusing. Always convert state name to state code,
        // so our form doesn't start throwing errors
      }

      const existingTaxIdValues = {
        ...defaultInitialValues,
        ...(country && { countryCode: country }),
        ...(postalCode && { postalCode }),
        ...(addr && { streetAddress: addr }),
        ...(extendedAddr && { streetAddressTwo: extendedAddr }),
        ...(city && { city }),
        ...(stateCode && { stateProvince: stateCode }),
        ...(company && { companyName: company })
      }

      switch (existingTaxIdValues.countryCode) {
        case 'CA':
          if (taxId) (existingTaxIdValues.canadaFederalTaxId = taxId)
          if (secondaryTaxId) (existingTaxIdValues.canadaProvincialTaxId = secondaryTaxId)
          break
        case 'AU':
          existingTaxIdValues.australiaTaxId = taxId
          break
        default:
          existingTaxIdValues.taxId = taxId
          break
      }

      return existingTaxIdValues
    }
    // Set form to existing shipping address if tax ID and shipping address exist

    if (initialValues === defaultInitialValues && billingAddress?.country) {
      return { ...defaultInitialValues, countryCode: billingAddress.country }
    } // Set country to billing address on file if form is in its default state

    return initialValues
  }

  return (
    <Popup
      onClose={handleClose}
      size="medium"
      withCloseButton
    >
      <Header>
        <Title>
          {I18n.t('publisher.billing.vat_id_popup.title')}
        </Title>
        <Subtitle>
          {I18n.t('publisher.billing.vat_id_popup.subtitle')}
        </Subtitle>
      </Header>
      <Form
        initialValues={initialValuesOrBillingCountry()}
        onSubmit={handleValidation}
        validationSchema={validationSchema}
      >
        <>
          <Body>
            <TaxForm />
          </Body>

          <FormFooter />
        </>
      </Form>
    </Popup>
  )
}
